import {
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AnalysisService,
  ConsumptionType,
  ProductionType,
} from '../../../../services/analysis.service';
import { CommonService } from '../../../../services/common.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CalculatorResultWrapper } from '../../../models/analysis/calculator-result-wrapper';
import { ResultChartsService } from '../../../../services/result-charts.service';
import { finalize, forkJoin, mergeMap, Observable, take, tap } from 'rxjs';
import { LoaderService } from '../../../../services/loader.service';
import { UnsubscribeOnDestroyAdapter } from '../../../adapters/unsubscibe-on-destroy.adapter';
import { ProjectRequestData } from '../model/power-plant-request-data';
import { PowerPlant } from '../model/power-plant';
import { Util } from '../../../models/util';
import { PdfConclusionService } from './pdf-conclusion.service';
import { TranslocoService } from '@ngneat/transloco';
import { fileSizeValidator, fileTypeValidator } from '../../../validators';
import { PowerPlantFormData } from '../model/power-plant-form-data';
import { PeakPowerChartComponent } from '../visual-data-components/peak-power-chart/peak-power-chart.component';
import { AuthService } from 'src/services/auth/auth.service';
import { UserRole } from 'src/common/models/user/user-role.enum';

@Component({
  selector: 'psc-pdf-download',
  templateUrl: './pdf-download.component.html',
})
export class PdfDownloadComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit
{
  private _calculatorResultWrapper: CalculatorResultWrapper;

  @Input() advanced: boolean = true;
  @Input() powerPlant: PowerPlant;

  @Input() set calculatorResultWrapper(value: CalculatorResultWrapper) {
    if (!value) {
      return;
    }
    this._calculatorResultWrapper = value;
    const conclusion = this.pdfConclusionService.getConclusion(value);
    this.formGroup.controls['conclusion'].setValue(conclusion);
    this.changeDetectorRef.detectChanges();
  }

  get calculatorResultWrapper(): CalculatorResultWrapper {
    return this._calculatorResultWrapper;
  }

  protected readonly ProductionType: typeof ProductionType = ProductionType;

  showPdfDialog: boolean;
  formGroup: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private analysisService: AnalysisService,
    private commonService: CommonService,
    private resultChartsService: ResultChartsService,
    private loaderService: LoaderService,
    private changeDetectorRef: ChangeDetectorRef,
    private zone: NgZone,
    private pdfConclusionService: PdfConclusionService,
    private translocoService: TranslocoService,
    private authService: AuthService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.formGroup = this.formBuilder.group({
      project_title: [this.powerPlant?.projectName || '', Validators.required],
      investor: [
        this.powerPlant?.investorName || '',
        this.powerPlant ? [] : Validators.required,
      ],
      address: [this.powerPlant?.address || '', Validators.required],
      image: [
        '',
        [fileTypeValidator(['jpg', 'jpeg', 'png']), fileSizeValidator(2048)],
      ],
      conclusion: [
        this.pdfConclusionService.getConclusion(this.calculatorResultWrapper),
        Validators.required,
      ],
      expanded: false,
    });
  }

  submitPDFData() {
    if (!this.formGroup.valid) {
      return;
    }
    this.downloadPDF();
  }

  downloadPDF() {
    this.loaderService.showLoader();

    let requestData: ProjectRequestData;
    if (this.powerPlant) {
      requestData = {
        projectName: this.powerPlant.projectName,
        investorName: this.powerPlant.investorName,
        conclusion: this.formGroup.controls['conclusion']?.value,
        address: this.powerPlant.address,
        language: this.translocoService.getActiveLang(),
        analysisId: this.powerPlant.analysisId,
      };
    } else {
      requestData = {
        projectName: this.formGroup.controls['project_title']?.value,
        investorName: this.formGroup.controls['investor']?.value,
        conclusion: this.formGroup.controls['conclusion']?.value,
        address: this.formGroup.controls['address']?.value,
        language: this.translocoService.getActiveLang(),
      };
    }
    requestData.expanded = this.formGroup.controls['expanded']?.value;

    let locationImage: Blob = new Blob();
    if (this.advanced && !this.powerPlant) {
      const formValueImage = this.formGroup.controls['image'].value[0];
      if (formValueImage) {
        locationImage = formValueImage;
      }
    } else if (this.powerPlant) {
      locationImage = Util.b64toBlob(this.powerPlant.locationPicture);
    }

    const powerPlantFormData: PowerPlantFormData = new PowerPlantFormData(
      requestData,
      this.calculatorResultWrapper,
      locationImage
    );

    const observables: Observable<any>[] = [];
    observables.push(
      this.resultChartsService.chartImageGenerated.annualGraph.pipe(
        take(1),
        tap((blob) => {
          powerPlantFormData.formData.append(
            'annualGraph',
            blob,
            'annualGraph.png'
          );
        })
      )
    );
    observables.push(
      this.resultChartsService.chartImageGenerated.ROIGraph.pipe(
        take(1),
        tap((blob) => {
          powerPlantFormData.formData.append('ROIGraph', blob, 'ROIGraph.png');
        })
      )
    );
    if (
      PeakPowerChartComponent.isPeakPowerChartShown(
        this.calculatorResultWrapper.result
      )
    ) {
      observables.push(
        this.resultChartsService.chartImageGenerated.annualPeakPowerGraph.pipe(
          take(1),
          tap((blob) => {
            powerPlantFormData.formData.append(
              'annualPeakPowerGraph',
              blob,
              'annualPeakPowerGraph.png'
            );
          })
        )
      );
    } else {
      powerPlantFormData.formData.append(
        'annualPeakPowerGraph',
        new Blob(),
        'empty.png'
      );
    }
    if (
      this.calculatorResultWrapper.consumptionInputMethod ===
      ConsumptionType.HOURLY
    ) {
      observables.push(
        this.resultChartsService.chartImageGenerated.monthlyGraph.pipe(
          take(1),
          tap((blobs) => {
            for (let i = 0; i < blobs.length; i++) {
              const blob = blobs[i];
              powerPlantFormData.formData.append(
                'monthlyGraph',
                blob,
                `monthlyGraph${i}.png`
              );
            }
          })
        )
      );
    } else {
      powerPlantFormData.formData.append(
        'monthlyGraph',
        new Blob(),
        'empty.png'
      );
    }

    this.subs.sink = forkJoin(observables)
      .pipe(
        mergeMap(() =>
          this.analysisService.getReport(powerPlantFormData.formData)
        ),
        finalize(() => {
          // Zone and change detector ref exist because the DOM wasn't updating properly
          // For some reason forkJoin creates issues with change detection
          // zone is for hiding the loader and change detector is for other changes
          this.zone.run(() => {
            this.loaderService.hideLoader();
          });
        })
      )
      .subscribe((blob) => {
        this.commonService.downloadFile(blob);
        this.initForm();
        this.showPdfDialog = false;
        this.changeDetectorRef.detectChanges();
      });

    Object.values(this.resultChartsService.generateChartImageRequest).forEach(
      (subject) => subject.next()
    );
  }

  downloadPdfClicked() {
      this.showPdfDialog = true;
    }
}
